<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { AvatarRootProps } from 'reka-ui'
import type { HTMLAttributes, InjectionKey, Ref } from 'vue'
import { cva } from 'class-variance-authority'

export const avatarVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
  {
    variants: {
      size: {
        '2xs': 'size-4',
        xs: 'size-5',
        sm: 'size-6',
        md: 'size-8',
        lg: 'size-12',
      },
      bordered: {
        true: 'border border-default bg-white',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
)

export type AvatarVariants = VariantProps<typeof avatarVariants>
export type AvatarSize = NonNullable<AvatarVariants['size']>

export interface AvatarProps extends AvatarRootProps {
  size?: AvatarVariants['size']
  bordered?: boolean
  class?: HTMLAttributes['class']
}

export interface AvatarContext {
  size: Ref<AvatarSize>
}

export const AVATAR_KEY: InjectionKey<AvatarContext> = Symbol('avatar')
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import { AvatarRoot, useForwardProps } from 'reka-ui'
import { computed, provide } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<AvatarProps>(), {
  size: 'sm',
})

const delegatedProps = reactiveOmit(props, 'class', 'size', 'bordered')
const forwarded = useForwardProps(delegatedProps)

provide(AVATAR_KEY, {
  size: computed(() => props.size ?? 'sm'),
})
</script>

<template>
  <AvatarRoot
    data-slot="avatar"
    :data-size="props.size"
    v-bind="forwarded"
    :class="cn(avatarVariants({ size: props.size, bordered: props.bordered }), props.class)"
  >
    <slot />
  </AvatarRoot>
</template>
